import { keepPreviousData, queryOptions } from '@tanstack/react-query'

import type { DeletePreview } from '@/bindings/DeletePreview'
import type { DeleteRestaurantRequest } from '@/bindings/DeleteRestaurantRequest'
import type { HoldRequest } from '@/bindings/HoldRequest'
import type { RestaurantDeleted } from '@/bindings/RestaurantDeleted'
import type { RestaurantDetail } from '@/bindings/RestaurantDetail'
import type { RestaurantListResponse } from '@/bindings/RestaurantListResponse'
import type { RestaurantStatistics } from '@/bindings/RestaurantStatistics'
import type { SlugAvailabilityResponse } from '@/bindings/SlugAvailabilityResponse'
import type { Span } from '@/features/back-office/statistics-params'
import { request } from '@/lib/api'
import type { System } from '@/lib/auth-store'
import { PAGE_SIZE } from '@/lib/paging'

/** The list's one filter (K4): deactivated restaurants with no hold still running, oldest deactivation first. */
export type RestaurantListFilter = 'deletable'

/** `all` prefixes every restaurant query, so one invalidation refreshes list and detail. */
export const restaurantKeys = {
  all: ['platform', 'restaurants'] as const,
  list: (page: number, filter?: RestaurantListFilter) => ['platform', 'restaurants', 'list', page, filter ?? 'all'] as const,
  detail: (id: string) => ['platform', 'restaurants', 'detail', id] as const,
  statistics: (id: string, query: string) => ['platform', 'restaurants', 'statistics', id, query] as const,
  availability: (system: System, slug: string) => [system, 'slug-availability', slug] as const,
}

export function restaurantPath(id: string, suffix = '') {
  return `/platform/restaurants/${id}${suffix}`
}

export function restaurantListQuery(page: number, filter?: RestaurantListFilter) {
  const query = `page=${page}&per_page=${PAGE_SIZE}${filter ? `&filter=${filter}` : ''}`
  return queryOptions({
    queryKey: restaurantKeys.list(page, filter),
    queryFn: () => request<RestaurantListResponse>(`/platform/restaurants?${query}`),
  })
}

/** K4: the hold; `hold_until: null` clears it. */
export function saveHold(id: string, body: HoldRequest) {
  return request<void>(restaurantPath(id, '/hold'), { method: 'PUT', body })
}

/** K4: what the delete would remove, or the 409 that says why it cannot run. */
export function previewDelete(id: string) {
  return request<DeletePreview>(restaurantPath(id, '/delete-preview'), { method: 'POST' })
}

/** K4: the hard delete, confirmed by the typed slug. */
export function deleteRestaurant(id: string, slug: string) {
  const body: DeleteRestaurantRequest = { slug }
  return request<RestaurantDeleted>(restaurantPath(id), { method: 'DELETE', body })
}

export function restaurantDetailQuery(id: string) {
  return queryOptions({
    queryKey: restaurantKeys.detail(id),
    queryFn: () => request<RestaurantDetail>(restaurantPath(id)),
  })
}

/** Order counts per branch and status over the span; the last month stays up while the next loads. */
export function restaurantStatisticsQuery(id: string, span: Span) {
  const query = new URLSearchParams({ from: span.from, to: span.to }).toString()
  return queryOptions({
    queryKey: restaurantKeys.statistics(id, query),
    queryFn: () => request<RestaurantStatistics>(restaurantPath(id, `/statistics?${query}`)),
    placeholderData: keepPreviousData,
  })
}

/**
 * Fresh every time: the verdict is only as good as the moment it was asked.
 * The same probe answers under either session: the platform's create form
 * and the self-serve one (K1).
 */
export function slugAvailabilityQuery(slug: string, system: System = 'platform') {
  const path = system === 'platform' ? '/platform/slug-availability' : '/restaurants/slug-availability'
  return queryOptions({
    queryKey: restaurantKeys.availability(system, slug),
    queryFn: () => request<SlugAvailabilityResponse>(`${path}?slug=${encodeURIComponent(slug)}`),
    staleTime: 0,
  })
}
